import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';

/**
 * Admin — Consent & Legal Acceptance Audit tab.
 *
 * Displays:
 *   • Filterable list of consent records (paginated)
 *   • Bulk CSV export (respects current filters)
 *   • Per-user PDF export (single-user, formal report)
 *   • Retention posture summary
 *   • Recent admin export events (audit-of-the-audit)
 *
 * Backend endpoints used:
 *   GET  /api/admin/consent/records
 *   GET  /api/admin/consent/records/export.csv
 *   GET  /api/admin/consent/records/{userId}/export.pdf
 *   GET  /api/admin/consent/retention-summary
 *   GET  /api/admin/consent/export-audit
 *   GET  /api/admin/consent/documents
 */

const DOC_TYPES = [
  { value: '', label: 'All document types' },
  { value: 'terms', label: 'Terms of Service' },
  { value: 'privacy_notice', label: 'Privacy Notice' },
  { value: 'marketing_email', label: 'Marketing email' },
  { value: 'product_updates', label: 'Product updates' },
];

const EVENT_TYPES = [
  { value: '', label: 'All events' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'opted_in', label: 'Opted in' },
  { value: 'opted_out', label: 'Opted out' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

const PAGE_SIZE = 50;

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
  } catch {
    return iso;
  }
}

function EventBadge({ event }) {
  const styles = {
    accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    acknowledged: 'bg-sky-50 text-sky-700 border-sky-200',
    opted_in: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    opted_out: 'bg-slate-50 text-slate-600 border-slate-200',
    withdrawn: 'bg-amber-50 text-amber-800 border-amber-200',
  };
  const cls = styles[event] || styles.opted_out;
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cls}`} data-testid={`event-badge-${event}`}>
      {event}
    </span>
  );
}

export default function ConsentAuditTab({ api }) {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    user_id: '', document_type: '', event_type: '', start_date: '', end_date: '',
  });
  const [retentionSummary, setRetentionSummary] = useState([]);
  const [exportAudit, setExportAudit] = useState([]);
  const [documents, setDocuments] = useState([]);

  const buildQuery = useCallback(() => {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) q.append(k, v); });
    return q;
  }, [filters]);

  const loadRecords = useCallback(async (newOffset = 0) => {
    setLoading(true);
    try {
      const q = buildQuery();
      q.append('limit', String(PAGE_SIZE));
      q.append('offset', String(newOffset));
      const r = await api.get(`/admin/consent/records?${q.toString()}`);
      setRecords(r.data.records || []);
      setTotal(r.data.total || 0);
      setOffset(newOffset);
    } catch (e) {
      toast.error('Failed to load consent records');
    } finally {
      setLoading(false);
    }
  }, [api, buildQuery]);

  const loadRetention = useCallback(async () => {
    try {
      const r = await api.get('/admin/consent/retention-summary');
      setRetentionSummary(r.data.summary || []);
    } catch (e) { /* silent */ }
  }, [api]);

  const loadExportAudit = useCallback(async () => {
    try {
      const r = await api.get('/admin/consent/export-audit?limit=20');
      setExportAudit(r.data.events || []);
    } catch (e) { /* silent */ }
  }, [api]);

  const loadDocuments = useCallback(async () => {
    try {
      const r = await api.get('/admin/consent/documents');
      setDocuments(r.data.documents || []);
    } catch (e) { /* silent */ }
  }, [api]);

  useEffect(() => { loadRecords(0); }, [loadRecords]);
  useEffect(() => { loadRetention(); loadExportAudit(); loadDocuments(); }, [loadRetention, loadExportAudit, loadDocuments]);

  const handleCsvExport = async () => {
    try {
      const q = buildQuery();
      const url = `/admin/consent/records/export.csv?${q.toString()}`;
      const res = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `consent_records_${new Date().toISOString().slice(0,19).replace(/[:T]/g, '')}Z.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('CSV exported');
      loadExportAudit();
    } catch (e) {
      toast.error('CSV export failed');
    }
  };

  const handlePdfExport = async (userId) => {
    try {
      const res = await api.get(`/admin/consent/records/${userId}/export.pdf`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `consent_audit_${userId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('PDF exported');
      loadExportAudit();
    } catch (e) {
      toast.error('PDF export failed');
    }
  };

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6" data-testid="admin-consent-audit-tab">
      {/* Retention summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="retention-summary">
        {retentionSummary.map((s) => (
          <div key={s.category} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`retention-card-${s.category}`}>
            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{s.category.replace(/_/g, ' ')}</div>
            <div className="mt-1 text-2xl font-bold text-[#0f1b3d]">{s.totalRecords}</div>
            <div className="text-[12px] text-slate-500 mt-2 leading-snug">{s.purpose}</div>
            <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
              <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                {s.retentionDays === null ? 'retention: indefinite' : `${s.retentionDays} days`}
              </span>
              <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                scheduled: {s.recordsWithScheduledDeletion}
              </span>
              {s.dueForDeletionNext30Days > 0 && (
                <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">
                  due in 30d: {s.dueForDeletionNext30Days}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Document registry summary */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="legal-documents-summary">
        <h3 className="text-sm font-semibold text-[#0f1b3d]">Published legal documents</h3>
        <div className="mt-2 divide-y divide-slate-100">
          {documents.map((d) => (
            <div key={d.id} className="py-2 flex items-center justify-between text-[13px]" data-testid={`legal-doc-row-${d.type}-${d.version}`}>
              <div>
                <span className="font-semibold text-slate-800">{d.type}</span>
                <span className="ml-2 text-slate-500">v{d.version}</span>
                {d.isCurrent && <span className="ml-2 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-semibold border border-emerald-200">CURRENT</span>}
                {d.requiresReacceptance && <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 text-[10px] font-semibold border border-amber-200">RE-ACCEPTANCE REQUIRED</span>}
              </div>
              <div className="text-slate-500 text-[11px]">{formatDate(d.effectiveDate)}</div>
            </div>
          ))}
          {documents.length === 0 && <div className="py-4 text-center text-slate-500 text-sm">No documents published yet.</div>}
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="consent-filters">
        <h3 className="text-sm font-semibold text-[#0f1b3d]">Consent records</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <input
            className="col-span-2 md:col-span-1 px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
            placeholder="User ID"
            value={filters.user_id}
            onChange={(e) => setFilter('user_id', e.target.value)}
            data-testid="filter-user-id"
          />
          <select
            className="px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
            value={filters.document_type}
            onChange={(e) => setFilter('document_type', e.target.value)}
            data-testid="filter-document-type"
          >
            {DOC_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select
            className="px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
            value={filters.event_type}
            onChange={(e) => setFilter('event_type', e.target.value)}
            data-testid="filter-event-type"
          >
            {EVENT_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <input
            type="date"
            className="px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
            value={filters.start_date}
            onChange={(e) => setFilter('start_date', e.target.value)}
            data-testid="filter-start-date"
          />
          <input
            type="date"
            className="px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
            value={filters.end_date}
            onChange={(e) => setFilter('end_date', e.target.value)}
            data-testid="filter-end-date"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => loadRecords(0)}
            className="px-3.5 py-1.5 rounded-full text-[12px] font-semibold bg-[#0f1b3d] text-white"
            data-testid="apply-filters-btn"
          >
            Apply filters
          </button>
          <button
            onClick={() => { setFilters({user_id:'',document_type:'',event_type:'',start_date:'',end_date:''}); setTimeout(() => loadRecords(0), 0); }}
            className="px-3.5 py-1.5 rounded-full text-[12px] font-semibold bg-white border border-slate-200 text-slate-600"
            data-testid="reset-filters-btn"
          >
            Reset
          </button>
          <button
            onClick={handleCsvExport}
            className="px-3.5 py-1.5 rounded-full text-[12px] font-semibold bg-emerald-600 text-white"
            data-testid="export-csv-btn"
          >
            Export filtered as CSV
          </button>
        </div>
      </div>

      {/* Records table */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto" data-testid="consent-records-table">
        <table className="min-w-full text-[12px]">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wide">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Timestamp</th>
              <th className="text-left px-3 py-2 font-semibold">User</th>
              <th className="text-left px-3 py-2 font-semibold">Event</th>
              <th className="text-left px-3 py-2 font-semibold">Document</th>
              <th className="text-left px-3 py-2 font-semibold">Change</th>
              <th className="text-left px-3 py-2 font-semibold">Source</th>
              <th className="text-left px-3 py-2 font-semibold">Retention</th>
              <th className="text-right px-3 py-2 font-semibold">PDF</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {records.map((r) => (
              <tr key={r.id} data-testid={`consent-row-${r.id}`}>
                <td className="px-3 py-2 whitespace-nowrap text-slate-600">{formatDate(r.timestamp)}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-500" title={r.userId}>{(r.userId || '').slice(0, 8)}</td>
                <td className="px-3 py-2"><EventBadge event={r.eventType} /></td>
                <td className="px-3 py-2 text-slate-700">
                  {r.documentType}
                  {r.documentVersion && r.documentVersion !== 'live' && <span className="ml-1 text-slate-400 text-[10px]">v{r.documentVersion}</span>}
                </td>
                <td className="px-3 py-2 text-slate-600">
                  {r.previousValue ?? '—'} <span className="text-slate-400">→</span> {r.newValue}
                </td>
                <td className="px-3 py-2 text-slate-600">{r.source}</td>
                <td className="px-3 py-2 text-slate-500 text-[11px]">{r.retentionCategory}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => handlePdfExport(r.userId)}
                    className="text-[11px] px-2 py-1 rounded-full bg-white border border-slate-200 text-slate-600 hover:text-[#0f1b3d]"
                    data-testid={`export-pdf-btn-${r.userId}`}
                  >
                    PDF
                  </button>
                </td>
              </tr>
            ))}
            {!loading && records.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-500" data-testid="consent-records-empty">No records match the filters.</td></tr>
            )}
            {loading && (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-500">Loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-[12px] text-slate-500" data-testid="pagination">
        <div>Showing {records.length === 0 ? 0 : offset + 1}–{offset + records.length} of {total}</div>
        <div className="flex gap-2">
          <button
            className="px-3 py-1 rounded-full bg-white border border-slate-200 disabled:opacity-40"
            disabled={offset === 0}
            onClick={() => loadRecords(Math.max(0, offset - PAGE_SIZE))}
            data-testid="page-prev-btn"
          >Prev</button>
          <button
            className="px-3 py-1 rounded-full bg-white border border-slate-200 disabled:opacity-40"
            disabled={offset + records.length >= total}
            onClick={() => loadRecords(offset + PAGE_SIZE)}
            data-testid="page-next-btn"
          >Next</button>
        </div>
      </div>

      {/* Admin export audit — audit-of-the-audit */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="export-audit-log">
        <h3 className="text-sm font-semibold text-[#0f1b3d]">Recent admin exports (audit of the audit)</h3>
        <div className="mt-2 divide-y divide-slate-100">
          {exportAudit.map((e) => (
            <div key={e.id} className="py-2 flex flex-wrap items-center gap-3 text-[12px]" data-testid={`export-audit-row-${e.id}`}>
              <span className="text-slate-500 whitespace-nowrap">{formatDate(e.timestamp)}</span>
              <span className="px-2 py-0.5 rounded bg-slate-100 uppercase font-semibold text-[10px]">{e.exportType}</span>
              <span className="text-slate-700 font-mono text-[11px]">{e.target}</span>
              <span className="text-slate-500">{e.recordCount} rows</span>
              <span className="text-slate-500 ml-auto">{e.adminEmail}</span>
            </div>
          ))}
          {exportAudit.length === 0 && <div className="py-4 text-center text-slate-500 text-sm">No exports yet.</div>}
        </div>
      </div>
    </div>
  );
}
